import React from 'react';
import { useTranslation } from 'react-i18next';
import { MediaItem } from '../../../../types';

interface QuickEffectsSectionProps {
  mediaMaker: MediaItem;
  setMediaItems: React.Dispatch<React.SetStateAction<MediaItem[]>>;
  styles: any; // CSS module styles
}

export const QuickEffectsSection: React.FC<QuickEffectsSectionProps> = ({
  mediaMaker,
  setMediaItems,
  styles,
}) => {
  const { t } = useTranslation(['mediaTab']);

  const handleEffectToggle = (effectType: 'saturation' | 'sepia' | 'vignette' | 'contrast', value: number) => {
    setMediaItems((prevMediaItems) =>
      prevMediaItems.map((mediaItem) =>
        mediaItem.id === mediaMaker.id
          ? {
              ...mediaItem,
              [effectType]: mediaItem[effectType] === value ? undefined : value,
            }
          : mediaItem
      )
    );
  };

  const handleResetAllEffects = () => {
    setMediaItems((prevMediaItems) =>
      prevMediaItems.map((mediaItem) =>
        mediaItem.id === mediaMaker.id
          ? {
              ...mediaItem,
              color: undefined,
              saturation: undefined,
              sepia: undefined,
              vignette: undefined,
              contrast: undefined,
              brightness: undefined,
            }
          : mediaItem
      )
    );
  };

  const hasAnyEffects =
    mediaMaker.color ||
    mediaMaker.saturation !== undefined ||
    mediaMaker.sepia !== undefined ||
    mediaMaker.vignette !== undefined ||
    mediaMaker.contrast !== undefined ||
    mediaMaker.brightness !== undefined;

  return (
    <div>
      <label className={styles.label} style={{ marginBottom: '0.75rem' }}>
        {t('mediaTab.quickEffects.title')}
      </label>

      {/* 2x2 Grid for quick effects */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '0.5rem',
          marginBottom: '0.75rem',
        }}
      >
        {/* Black & White */}
        <button
          onClick={() => handleEffectToggle('saturation', 0)}
          style={{
            padding: '0.6rem',
            background: mediaMaker.saturation === 0 ? '#4a4a4a' : '#3a3a3a',
            color: '#e0e0e0',
            border: mediaMaker.saturation === 0 ? '2px solid #666' : '1px solid #555',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '0.85rem',
            fontWeight: mediaMaker.saturation === 0 ? '600' : '400',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
          }}
          onMouseEnter={(e) => {
            if (mediaMaker.saturation !== 0) {
              e.currentTarget.style.background = '#4a4a4a';
            }
          }}
          onMouseLeave={(e) => {
            if (mediaMaker.saturation !== 0) {
              e.currentTarget.style.background = '#3a3a3a';
            }
          }}
        >
          <span style={{ fontSize: '1.2rem' }}>⚫</span>
          <span>{t('mediaTab.quickEffects.blackAndWhite')}</span>
        </button>

        {/* Sepia */}
        <button
          onClick={() => handleEffectToggle('sepia', 0.7)}
          style={{
            padding: '0.6rem',
            background: mediaMaker.sepia === 0.7 ? '#4a4a4a' : '#3a3a3a',
            color: '#e0e0e0',
            border: mediaMaker.sepia === 0.7 ? '2px solid #666' : '1px solid #555',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '0.85rem',
            fontWeight: mediaMaker.sepia === 0.7 ? '600' : '400',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
          }}
          onMouseEnter={(e) => {
            if (mediaMaker.sepia !== 0.7) {
              e.currentTarget.style.background = '#4a4a4a';
            }
          }}
          onMouseLeave={(e) => {
            if (mediaMaker.sepia !== 0.7) {
              e.currentTarget.style.background = '#3a3a3a';
            }
          }}
        >
          <span style={{ fontSize: '1.2rem' }}>🟤</span>
          <span>{t('mediaTab.quickEffects.sepia')}</span>
        </button>

        {/* Vignette */}
        <button
          onClick={() => handleEffectToggle('vignette', 0.7)}
          style={{
            padding: '0.6rem',
            background: mediaMaker.vignette === 0.7 ? '#4a4a4a' : '#3a3a3a',
            color: '#e0e0e0',
            border: mediaMaker.vignette === 0.7 ? '2px solid #666' : '1px solid #555',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '0.85rem',
            fontWeight: mediaMaker.vignette === 0.7 ? '600' : '400',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
          }}
          onMouseEnter={(e) => {
            if (mediaMaker.vignette !== 0.7) {
              e.currentTarget.style.background = '#4a4a4a';
            }
          }}
          onMouseLeave={(e) => {
            if (mediaMaker.vignette !== 0.7) {
              e.currentTarget.style.background = '#3a3a3a';
            }
          }}
        >
          <span style={{ fontSize: '1.2rem' }}>🌓</span>
          <span>{t('mediaTab.quickEffects.vignette')}</span>
        </button>

        {/* High Contrast */}
        <button
          onClick={() => handleEffectToggle('contrast', 1.5)}
          style={{
            padding: '0.6rem',
            background: mediaMaker.contrast === 1.5 ? '#4a4a4a' : '#3a3a3a',
            color: '#e0e0e0',
            border: mediaMaker.contrast === 1.5 ? '2px solid #666' : '1px solid #555',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '0.85rem',
            fontWeight: mediaMaker.contrast === 1.5 ? '600' : '400',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
          }}
          onMouseEnter={(e) => {
            if (mediaMaker.contrast !== 1.5) {
              e.currentTarget.style.background = '#4a4a4a';
            }
          }}
          onMouseLeave={(e) => {
            if (mediaMaker.contrast !== 1.5) {
              e.currentTarget.style.background = '#3a3a3a';
            }
          }}
        >
          <span style={{ fontSize: '1.2rem' }}>⚡</span>
          <span>{t('mediaTab.quickEffects.highContrast')}</span>
        </button>
      </div>

      {/* Reset All Effects Button */}
      {hasAnyEffects && (
        <button
          onClick={handleResetAllEffects}
          style={{
            width: '100%',
            marginTop: '0.5rem',
            padding: '0.5rem',
            background: '#3a3a3a',
            color: '#e0e0e0',
            border: '1px solid #555',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '0.85rem',
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#4a4a4a';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = '#3a3a3a';
          }}
        >
          {t('mediaTab.quickEffects.resetAll')}
        </button>
      )}
    </div>
  );
};
